// A text's faces (#241 part 2): the families of live's font picker and presets, loaded from
// Google Fonts as live's are (the lead's decision, 2026-10-16): each family's stylesheet the first
// time a text uses it, then the faces its text needs. The app's own type (Inter, Space Grotesk)
// stays self-hosted and is never taken from here.
import type { FontFamily } from "../../api/generated/model";
import { googleFaceReady, googleStylesheet, loadGoogleFace, type GoogleFace } from "../../platform/google-fonts";

/** One of a family's styles: its weight and slant, named as live's Style select names it. */
export interface FontStyleChoice {
  weight: number;
  italic: boolean;
  label: string;
}

const EVERY = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/**
 * The families, A to Z as live's picker lists them, with the weights each offers upright and
 * slanted: live's catalog's styles, and Google Fonts' for the three preset families live's catalog
 * lacks (Abril Fatface, Big Shoulders Display, Sora). Each list is one Google Fonts serves.
 */
const CATALOG: readonly { family: FontFamily; upright: readonly number[]; italic: readonly number[] }[] = [
  { family: "Abril Fatface", upright: [400], italic: [] },
  { family: "Alegreya", upright: [400, 500, 600, 700, 800, 900], italic: [400, 500, 600, 700, 800, 900] },
  { family: "Alegreya Sans", upright: [100, 300, 400, 500, 700, 800, 900], italic: [100, 300, 400, 500, 700, 800, 900] },
  { family: "Aleo", upright: [300, 400, 700], italic: [300, 400, 700] },
  { family: "Arapey", upright: [400], italic: [400] },
  { family: "Archivo Narrow", upright: [400, 500, 600, 700], italic: [400, 500, 600, 700] },
  { family: "Asap Condensed", upright: [400, 500, 600, 700], italic: [400, 500, 600, 700] },
  { family: "Assistant", upright: [200, 300, 400, 500, 600, 700, 800], italic: [] },
  { family: "Bangers", upright: [400], italic: [] },
  { family: "Barlow", upright: EVERY, italic: EVERY },
  { family: "Big Shoulders Display", upright: EVERY, italic: [] },
  { family: "BioRhyme", upright: [200, 300, 400, 700, 800], italic: [] },
  { family: "Bitter", upright: EVERY, italic: EVERY },
  { family: "Brawler", upright: [400], italic: [] },
  { family: "Cabin", upright: [400, 500, 600, 700], italic: [400, 500, 600, 700] },
  { family: "Caladea", upright: [400, 700], italic: [400, 700] },
  { family: "Cardo", upright: [400, 700], italic: [400] },
  { family: "Carme", upright: [400], italic: [] },
  { family: "Chivo", upright: [300, 400, 700, 900], italic: [300, 400, 700, 900] },
  { family: "Cormorant", upright: [300, 400, 500, 600, 700], italic: [300, 400, 500, 600, 700] },
  { family: "DM Sans", upright: [400, 500, 700], italic: [400, 500, 700] },
  { family: "Domine", upright: [400, 500, 600, 700], italic: [] },
  { family: "Eczar", upright: [400, 500, 600, 700, 800], italic: [] },
  { family: "Encode Sans Semi Condensed", upright: EVERY, italic: [] },
  { family: "Enriqueta", upright: [400, 500, 600, 700], italic: [] },
  { family: "Fira Sans", upright: EVERY, italic: EVERY },
  { family: "Frank Ruhl Libre", upright: [300, 400, 500, 700, 900], italic: [] },
  { family: "Fraunces", upright: EVERY, italic: EVERY },
  { family: "Gelasio", upright: [400, 500, 600, 700], italic: [400, 500, 600, 700] },
  { family: "Headland One", upright: [400], italic: [] },
  { family: "IBM Plex Sans", upright: [100, 200, 300, 400, 500, 600, 700], italic: [100, 200, 300, 400, 500, 600, 700] },
  { family: "Inconsolata", upright: [200, 300, 400, 500, 600, 700, 800, 900], italic: [] },
  { family: "Inknut Antiqua", upright: [300, 400, 500, 600, 700, 800, 900], italic: [] },
  { family: "Inter", upright: EVERY, italic: [] },
  { family: "Josefin Sans", upright: [100, 200, 300, 400, 500, 600, 700], italic: [100, 200, 300, 400, 500, 600, 700] },
  { family: "Karla", upright: [200, 300, 400, 500, 600, 700, 800], italic: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Lato", upright: [100, 300, 400, 700, 900], italic: [100, 300, 400, 700, 900] },
  { family: "Libre Baskerville", upright: [400, 700], italic: [400] },
  { family: "Libre Franklin", upright: EVERY, italic: EVERY },
  { family: "Lora", upright: [400, 500, 600, 700], italic: [400, 500, 600, 700] },
  { family: "Maitree", upright: [200, 300, 400, 500, 600, 700], italic: [] },
  { family: "Manrope", upright: [200, 300, 400, 500, 600, 700, 800], italic: [] },
  { family: "Merriweather", upright: [300, 400, 700, 900], italic: [300, 400, 700, 900] },
  { family: "Montserrat", upright: EVERY, italic: EVERY },
  { family: "Nanum Gothic", upright: [400], italic: [] },
  { family: "Neuton", upright: [200, 300, 400, 700, 800], italic: [400] },
  { family: "Nunito", upright: [200, 300, 400, 500, 600, 700, 800, 900], italic: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Open Sans", upright: [300, 400, 500, 600, 700, 800], italic: [300, 400, 500, 600, 700, 800] },
  { family: "Oswald", upright: [200, 300, 400, 500, 600, 700], italic: [] },
  { family: "Playfair Display", upright: [400, 500, 600, 700, 800, 900], italic: [400, 500, 600, 700, 800, 900] },
  { family: "Poppins", upright: EVERY, italic: EVERY },
  { family: "Proza Libre", upright: [400, 500, 600, 700, 800], italic: [400, 500, 600, 700, 800] },
  { family: "PT Sans", upright: [400, 700], italic: [400, 700] },
  { family: "PT Serif", upright: [400, 700], italic: [400, 700] },
  { family: "Quattrocento", upright: [400, 700], italic: [] },
  { family: "Raleway", upright: EVERY, italic: EVERY },
  { family: "Roboto", upright: [100, 300, 400, 500, 700, 900], italic: [100, 300, 400, 500, 700, 900] },
  { family: "Roboto Condensed", upright: [300, 400, 700], italic: [300, 400, 700] },
  { family: "Rokkitt", upright: EVERY, italic: [] },
  { family: "Rubik", upright: [300, 400, 500, 600, 700, 800, 900], italic: [300, 400, 500, 600, 700, 800, 900] },
  { family: "Sora", upright: [100, 200, 300, 400, 500, 600, 700, 800], italic: [] },
  { family: "Source Code Pro", upright: [200, 300, 400, 500, 600, 700, 800, 900], italic: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Source Sans 3", upright: [200, 300, 400, 500, 600, 700, 800, 900], italic: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Source Sans Pro", upright: [200, 300, 400, 600, 700, 900], italic: [200, 300, 400, 600, 700, 900] },
  { family: "Source Serif Pro", upright: [200, 300, 400, 600, 700, 900], italic: [200, 300, 400, 600, 700, 900] },
  { family: "Space Grotesk", upright: [300, 400, 500, 600, 700], italic: [] },
  { family: "Space Mono", upright: [400, 700], italic: [400, 700] },
  { family: "Spectral", upright: [200, 300, 400, 500, 600, 700, 800], italic: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Syne", upright: [400, 500, 600, 700, 800], italic: [] },
  { family: "Teko", upright: [300, 400, 500, 600, 700], italic: [] },
  { family: "Ubuntu", upright: [300, 400, 500, 700], italic: [300, 400, 500, 700] },
  { family: "Work Sans", upright: EVERY, italic: EVERY },
  { family: "Zilla Slab", upright: [300, 400, 500, 600, 700], italic: [300, 400, 500, 600, 700] },
];

export const FONT_FAMILIES: readonly FontFamily[] = CATALOG.map((entry) => entry.family);

const WEIGHT_NAMES: Readonly<Record<number, string>> = {
  100: "Thin",
  200: "ExtraLight",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "SemiBold",
  700: "Bold",
  800: "ExtraBold",
  900: "Black",
};

/** A style's name as live's select shows it: "Bold", "Bold Italic", "Regular", "Italic". */
export function styleLabel(weight: number, italic: boolean): string {
  const name = WEIGHT_NAMES[weight] ?? String(weight);
  if (!italic) return name;
  return weight === 400 ? "Italic" : `${name} Italic`;
}

/** A family's styles, light to heavy, each weight upright then slanted. */
export function stylesOf(family: FontFamily): FontStyleChoice[] {
  const entry = CATALOG.find((each) => each.family === family);
  if (entry === undefined) return [];
  return EVERY.flatMap((weight) => [
    ...(entry.upright.includes(weight) ? [{ weight, italic: false, label: styleLabel(weight, false) }] : []),
    ...(entry.italic.includes(weight) ? [{ weight, italic: true, label: styleLabel(weight, true) }] : []),
  ]);
}

/** The family's style nearest to a weight and slant: the same if it has it (a family change). */
export function nearestStyle(family: FontFamily, weight: number, italic: boolean): FontStyleChoice {
  const styles = stylesOf(family);
  const slanted = styles.filter((style) => style.italic === italic);
  const pool = slanted.length > 0 ? slanted : styles;
  return pool.reduce((best, style) =>
    Math.abs(style.weight - weight) < Math.abs(best.weight - weight) ? style : best,
  );
}

/** The name a face is drawn by: Google Fonts' family name, as its stylesheet declares it. */
export function faceName(family: FontFamily): string {
  return family;
}

/**
 * A family's Google Fonts stylesheet: every style the picker lists for it (Google's order,
 * upright then slanted, light to heavy), with `display=swap`.
 */
export function stylesheetOf(family: FontFamily): string {
  return googleStylesheet(family, stylesOf(family));
}

/** A face a text needs: its family, weight and slant, and the characters it draws. */
export interface Face extends GoogleFace {
  family: FontFamily;
}

/**
 * Whether a face is ready for its text: its family's stylesheet is in and every face its
 * characters need is loaded (a text drawn before would show another face, so it waits).
 */
export function faceReady(face: Face): boolean {
  return googleFaceReady(face, stylesheetOf(face.family));
}

/**
 * Loads a face for its text: its family's stylesheet, then the faces those characters need. A
 * face that cannot load (offline, blocked) resolves anyway, and its text draws in the browser's
 * fallback face.
 */
export async function loadFace(face: Face): Promise<void> {
  await loadGoogleFace(face, stylesheetOf(face.family));
}

/** Loads every face; resolves when all are ready (or have failed to the fallback). */
export async function loadFaces(faces: readonly Face[]): Promise<void> {
  await Promise.all(faces.map(loadFace));
}
